/**
 * Company › Owned arches › one arche's TEAM (ADR-026 offboarding).
 *
 * A publisher row outlives every Company and workspace membership, so anyone
 * who has left the Company is flagged here — and can be revoked. Nothing is
 * revoked on its own (a contractor is a legitimate publisher), and revoking
 * asks first: it cannot be undone from here.
 */

import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import type { CompanyArcheTeamMemberDto, CompanyOwnedArcheDto } from '@/lib/api';
import { ARCHE_ROLE_LABEL } from '../../lib/arche-governance-labels';
import { useRevokeCompanyArcheCollaborator } from '../../hooks/useArcheGovernance';
import { describeError, personLabel } from './shared';

export function OwnedArcheTeam({
  companyId,
  arche,
  id,
}: {
  companyId: string;
  arche: CompanyOwnedArcheDto;
  /** For the toggle's `aria-controls`. */
  id?: string;
}) {
  const revoke = useRevokeCompanyArcheCollaborator(companyId);
  const [revoking, setRevoking] = useState<CompanyArcheTeamMemberDto | null>(null);

  if (arche.team.length === 0) {
    return (
      <p id={id} className="text-xs text-muted-foreground">
        Nobody is on this arche&apos;s team yet. Publishers are added from its page or the CLI.
      </p>
    );
  }

  async function confirmRevoke(m: CompanyArcheTeamMemberDto): Promise<void> {
    try {
      await revoke.mutateAsync({ archeId: arche.archeId, userId: m.userId });
      toast({ title: `Revoked ${personLabel(m)}’s access to ${arche.name}` });
    } catch (err) {
      toast({ title: 'Could not revoke', description: describeError(err), variant: 'destructive' });
    }
  }

  return (
    <>
      <ul id={id} className="divide-y rounded-md border text-xs" aria-label={`${arche.name} team`}>
        {arche.team.map((m) => (
          <li
            key={m.userId}
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5"
          >
            <span className="flex items-center gap-2">
              <span>{personLabel(m)}</span>
              <span className="text-muted-foreground">{ARCHE_ROLE_LABEL[m.role]}</span>
              {!m.affiliated && (
                <Badge
                  variant="destructive"
                  title="Not on this Company's roster and not in any of its linked workspaces"
                >
                  Not in this Company
                </Badge>
              )}
            </span>
            <Button
              size="sm"
              variant={m.affiliated ? 'ghost' : 'outline'}
              className="h-7 px-2 text-xs"
              disabled={revoke.isPending}
              aria-label={`Revoke ${personLabel(m)}’s access to ${arche.name}`}
              onClick={() => setRevoking(m)}
            >
              Revoke access
            </Button>
          </li>
        ))}
      </ul>
      <AlertDialog open={revoking !== null} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {revoking
                ? `Revoke ${personLabel(revoking)}’s access to ${arche.name}?`
                : 'Revoke access?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              They leave its team: they can no longer publish it, and if only its team and chosen
              people may use it, they lose that too unless they were given access some other way.
              Adding them back is done from the arche&apos;s page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const m = revoking;
                setRevoking(null);
                if (m) void confirmRevoke(m);
              }}
            >
              Revoke access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
